import React, { JSX, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getReport,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import { ReportKdsUtils } from "../../../../../../../../utils/reports/ReportKdsUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "KDS_REPORT";
const resourceType = "KDS_REPORT";
const type = "order_item_preparation_status_change";

const Report = ({ resource }: ListState): JSX.Element | null => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();
	const columnsFromLocalStorage = window.localStorage.getItem(`go_report.employees.columns`)?.split(",");

	let config = {
		reportConfigId: "kds_report",
		aggregatePrefix: "sales",
		type: "bill",
		chart: {
			title: t("modules.preparation_status_report.field.report.title"),
			lineTitle: t("modules.preparation_status_report.field.report_chart.title"),
			aggregate: "average_total_time",
			type: getChartType(),
			parseGroup: (group: string) => {
				if (group === "HOUR_OF_DAY") return "ORDER_ITEM_CREATED_AT_HOUR";
				if (group === "DAY_OF_MONTH") return "ORDER_ITEM_CREATED_AT_DAY";
				if (group === "DATE") return "ORDER_ITEM_CREATED_AT";
				if (group === "DAY_OF_WEEK") return "ORDER_ITEM_CREATED_AT_DAY_OF_WEEK";
				if (group === "MONTH") return "ORDER_ITEM_CREATED_AT_MONTH";
				return group;
			},
		},
		groups: [...ReportKdsUtils.getKdsGroups(t), ...ReportKdsUtils.getGroups(t), ...ReportShareUtils.getGroups(t)],
		selectedColumns: columnsFromLocalStorage ? columnsFromLocalStorage : ReportKdsUtils.getSelectedColumns(),
		selectedGroups: ["NONE"],
		groupsInfo: {
			...ReportKdsUtils.getGroupsInfo(t, organization),
			...ReportKdsUtils.getKdsGroupsInfo(t, organization),
			ORDER_NUMBER: (groupByValue: Record<string, any>) =>
				ReportKdsUtils.getOrderNumberRedirectLink(groupByValue.name, organization.id),
		},
		showPercent: true,
		columns: [...ReportKdsUtils.getColumns(t), ...ReportKdsUtils.getKdsColumns(t)],
		filters: [...ReportKdsUtils.getKdsFilters(t), ...ReportKdsUtils.getFilters(t)],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				groups: ["NONE"],
				columns: columnsFromLocalStorage ? columnsFromLocalStorage : ReportKdsUtils.getSelectedColumns(),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					organization.id,
					config.filters,
					type,
					sourceToken,
					params.groups === "NONE"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					organization.id,
					config.filters,
					type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				organization.id,
				config.filters,
				type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.preparation_status_report.header.title"),
			filename: t("modules.preparation_status_report.field.export_config_filename.title"),
			organization: `${organization.name}`,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const OrganizationReportsKdsPage = (): JSX.Element => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);

	useEffect(() => {
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} />
		</Suspense>
	);
};

export default OrganizationReportsKdsPage;
